import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { IconButton, Snackbar } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { CloseButton } from "../../components/CloseButton";
import { financeApi } from "../../lib/api";
import type { Tip } from "../../lib/api/tips";
import { queryKeys } from "../../lib/queryKeys";
import { TipContent } from "./TipContent";
import {
  TIPS_DISMISS_HOURS,
  useTipsDismissed,
} from "./useTipsDismissed";

const AUTO_ADVANCE_MS = 5000;

export function TipsCarouselView({
  items,
  initialTipId,
  onClose,
  autoAdvance = false,
}: {
  items: Tip[];
  initialTipId?: string | null;
  onClose?: () => void;
  autoAdvance?: boolean;
}) {
  const initialIndex = Math.max(
    0,
    items.findIndex((item) => item.id === initialTipId),
  );
  const [index, setIndex] = useState(initialIndex);
  const [autoAdvanceKey, setAutoAdvanceKey] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const tip = items[index] || items[0];

  useEffect(
    () =>
      setIndex(
        Math.max(
          0,
          items.findIndex((item) => item.id === initialTipId),
        ),
      ),
    [initialTipId, items],
  );
  useEffect(() => {
    if (!autoAdvance || detailsOpen || items.length <= 1) return;
    const id = window.setInterval(
      () => setIndex((current) => (current + 1) % items.length),
      AUTO_ADVANCE_MS,
    );
    return () => window.clearInterval(id);
  }, [autoAdvance, autoAdvanceKey, detailsOpen, items.length]);
  if (!tip) return null;

  const goToTip = (next: number) => {
    setIndex((next + items.length) % items.length);
    setAutoAdvanceKey((current) => current + 1);
  };
  const handleTouchEnd = (clientX: number) => {
    if (touchStart !== null && Math.abs(clientX - touchStart) > 36)
      goToTip(index + (clientX > touchStart ? -1 : 1));
    setTouchStart(null);
  };

  return (
    <section
      className="tips-carousel"
      aria-label="Tips"
      onTouchStart={(event) =>
        setTouchStart(event.changedTouches[0]?.clientX ?? null)
      }
      onTouchEnd={(event) =>
        handleTouchEnd(event.changedTouches[0]?.clientX ?? 0)
      }
    >
      <div className="tips-carousel-header">
        <span>
          <LightbulbOutlinedIcon aria-hidden="true" />
          Tips
        </span>
        {onClose ? (
          <CloseButton ariaLabel="Hide tips" onClick={onClose} />
        ) : null}
      </div>
      <div className="tips-carousel-body">
        <TipContent tip={tip} onDetailsOpenChange={setDetailsOpen} />
      </div>
      {items.length > 1 ? (
        <div className="tips-carousel-footer">
          <div className="tips-carousel-dots" aria-label="Tip position">
            {items.map((item, dotIndex) => (
              <button
                key={item.id}
                className={dotIndex === index ? "active" : ""}
                type="button"
                aria-label={`Show tip ${dotIndex + 1}`}
                aria-current={dotIndex === index}
                onClick={() => goToTip(dotIndex)}
              />
            ))}
          </div>
          <div className="tips-carousel-controls">
            <IconButton
              className="overview-icon-button"
              aria-label="Previous tip"
              onClick={() => goToTip(index - 1)}
            >
              <ArrowBackIosNewOutlinedIcon />
            </IconButton>
            <IconButton
              className="overview-icon-button"
              aria-label="Next tip"
              onClick={() => goToTip(index + 1)}
            >
              <ArrowForwardIosOutlinedIcon />
            </IconButton>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export function TipsCarousel() {
  const { dismissed, dismiss } = useTipsDismissed();
  const [dismissedSnackbarOpen, setDismissedSnackbarOpen] = useState(false);
  const query = useQuery({
    queryKey: queryKeys.tips,
    queryFn: financeApi.tips,
    staleTime: 5 * 60 * 1000,
  });
  if (!query.data?.length) return null;
  const dismissWithNotice = () => {
    dismiss();
    setDismissedSnackbarOpen(true);
  };
  return (
    <>
      {dismissed ? null : (
        <TipsCarouselView
          items={query.data}
          onClose={dismissWithNotice}
          autoAdvance
        />
      )}
      <Snackbar
        open={dismissedSnackbarOpen}
        message={`Tips won't be shown for ${TIPS_DISMISS_HOURS} hours.`}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setDismissedSnackbarOpen(false);
        }}
      />
    </>
  );
}
